import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { slugify, type PortalClient, type PortalProject, type PortalProposal } from "@/lib/portal-shared";
import {
  createPortalClient,
  createPortalProject,
  deletePortalClient,
  deletePortalProject,
  deletePortalProposal,
  ensureAdmin,
  listPortalAdmin,
  reorderPortalProposals,
  rotatePortalToken,
  updatePortalClient,
  updatePortalProject,
  updatePortalProposal,
  uploadPortalProposal,
} from "@/lib/portal.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin — PULSO33" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Internal PULSO33 administration area." },
    ],
  }),
  component: AdminPage,
});

type State = "loading" | "signed-out" | "denied" | "ready";

function AdminPage() {
  const [state, setState] = useState<State>("loading");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState<PortalClient[]>([]);
  const [projects, setProjects] = useState<PortalProject[]>([]);
  const [proposals, setProposals] = useState<PortalProposal[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [editing, setEditing] = useState<string>("");
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const check = useServerFn(ensureAdmin);
  const list = useServerFn(listPortalAdmin);
  const addClient = useServerFn(createPortalClient);
  const rmClient = useServerFn(deletePortalClient);
  const rotate = useServerFn(rotatePortalToken);
  const addProject = useServerFn(createPortalProject);
  const rmProject = useServerFn(deletePortalProject);
  const upload = useServerFn(uploadPortalProposal);
  const rmProposal = useServerFn(deletePortalProposal);
  const editClient = useServerFn(updatePortalClient);
  const editProject = useServerFn(updatePortalProject);
  const editProposal = useServerFn(updatePortalProposal);
  const reorder = useServerFn(reorderPortalProposals);


  async function refresh() {
    const data = await list({ data: undefined });
    setClients(data.clients);
    setProjects(data.projects);
    setProposals(data.proposals);
  }

  async function boot() {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      setState("signed-out");
      return;
    }
    try {
      const res = await check({ data: undefined });
      if (!res.isAdmin) {
        setState("denied");
        return;
      }
      await refresh();
      setState("ready");
    } catch {
      setState("denied");
    }
  }

  useEffect(() => {
    void boot();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") void boot();
    });
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: `${window.location.origin}/admin` },
    });
    setSending(false);
    if (error) toast.error(error.message);
    else toast.success("Magic link sent. Check your inbox.");
  }

  async function signOut() {
    await supabase.auth.signOut();
    setState("signed-out");
  }

  const clientProjects = useMemo(
    () => projects.filter((p) => p.client_id === selectedClient),
    [projects, selectedClient],
  );
  const projectProposals = useMemo(
    () => proposals.filter((p) => p.project_id === selectedProject),
    [proposals, selectedProject],
  );
  const currentClient = clients.find((c) => c.id === selectedClient);
  const currentProject = projects.find((p) => p.id === selectedProject);

  async function moveProposal(from: number, to: number) {
    const ordered = [...projectProposals];
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setDragIndex(null);
    const ids = ordered.map((p) => p.id);
    setProposals((prev) => [
      ...prev.filter((p) => p.project_id !== selectedProject),
      ...ordered.map((p, i) => ({ ...p, sort_order: i })),
    ]);
    try {
      await reorder({ data: { ids } });
      await refresh();
      toast.success("Order saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save order");
      await refresh();
    }
  }

  // Auto-open the first project of a selected client so proposals/uploads are reachable
  useEffect(() => {
    if (selectedClient && !selectedProject && clientProjects.length > 0) {
      setSelectedProject(clientProjects[0].id);
    }
  }, [selectedClient, selectedProject, clientProjects]);


  if (state === "loading") {
    return <Shell><p className="text-sm text-muted-foreground">Loading…</p></Shell>;
  }

  if (state === "signed-out" || state === "denied") {
    return (
      <Shell>
        <h1 className="font-display text-3xl text-foreground">Admin access</h1>
        {state === "denied" ? (
          <p className="mt-3 text-sm text-destructive">
            This account is not allowed here.{" "}
            <button onClick={signOut} className="underline">
              Sign out
            </button>
          </p>
        ) : null}
        <form onSubmit={sendMagicLink} className="mt-6 flex max-w-md flex-col gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@pulso33.com"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
          <button
            type="submit"
            disabled={sending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send magic link"}
          </button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-display text-3xl text-foreground">Client proposals</h1>
        <button onClick={signOut} className="text-xs text-muted-foreground underline">
          Sign out
        </button>
      </div>

      {/* Clients */}
      <section className="mt-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Clients</h2>
        <NewClientForm
          onCreate={async (name, notes) => {
            await addClient({ data: { name, slug: slugify(name), notes } });
            await refresh();
            toast.success("Client created");
          }}
        />
        <div className="mt-4 grid gap-2">
          {clients.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedClient(c.id);
                setSelectedProject("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSelectedClient(c.id);
                  setSelectedProject("");
                }
              }}
              className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                selectedClient === c.id ? "border-primary" : "border-border hover:border-primary/50"
              } bg-card`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-left">
                  <span className="block font-display text-lg text-foreground">{c.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    /{c.slug} · {projects.filter((p) => p.client_id === c.id).length} projects
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="underline"
                    onClick={() => setEditing(editing === c.id ? "" : c.id)}
                  >
                    {editing === c.id ? "Cancel" : "Edit"}
                  </button>
                  <button
                    className="underline"
                    onClick={() => {
                      const url = `${window.location.origin}/clients/${c.slug}/projects?t=${c.access_token}`;
                      void navigator.clipboard.writeText(url);
                      toast.success("Secure link copied");
                    }}
                  >
                    Copy link
                  </button>
                  <button
                    className="underline"
                    onClick={async () => {
                      await rotate({ data: { id: c.id } });
                      await refresh();
                      toast.success("Token rotated — old links no longer work");
                    }}
                  >
                    Rotate token
                  </button>
                  <button
                    className="text-destructive underline"
                    onClick={async () => {
                      if (!confirm(`Delete ${c.name} and all its projects?`)) return;
                      await rmClient({ data: { id: c.id } });
                      setSelectedClient("");
                      await refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {editing === c.id ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <EditForm
                    initial={[c.name, c.notes ?? ""]}
                    placeholders={["Client name", "Notes (optional)"]}
                    onSave={async ([name, notes]) => {
                      await editClient({ data: { id: c.id, name, notes } });
                      setEditing("");
                      await refresh();
                      toast.success("Client updated");
                    }}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>


      {/* Projects */}
      {currentClient ? (
        <section className="mt-12">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
            Projects · {currentClient.name}
          </h2>
          <NewProjectForm
            onCreate={async (name, description) => {
              await addProject({
                data: { clientId: currentClient.id, name, slug: slugify(name), description },
              });
              await refresh();
              toast.success("Project created");
            }}
          />
          <div className="mt-4 grid gap-2">
            {clientProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No projects yet — add one above to start uploading proposals.
              </p>
            ) : null}
            {clientProjects.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedProject(p.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedProject(p.id);
                }}
                className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                  selectedProject === p.id ? "border-primary" : "border-border hover:border-primary/50"
                } bg-card`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-left">
                    <span className="block font-display text-lg text-foreground">{p.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      /{p.slug} · {proposals.filter((x) => x.project_id === p.id).length} proposals
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="underline"
                      onClick={() => setEditing(editing === p.id ? "" : p.id)}
                    >
                      {editing === p.id ? "Cancel" : "Edit"}
                    </button>
                    <button
                      className="text-destructive underline"
                      onClick={async () => {
                        if (!confirm(`Delete project ${p.name}?`)) return;
                        await rmProject({ data: { id: p.id } });
                        setSelectedProject("");
                        await refresh();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {editing === p.id ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <EditForm
                      initial={[p.name, p.description ?? ""]}
                      placeholders={["Project name", "Description (optional)"]}
                      onSave={async ([name, description]) => {
                        await editProject({ data: { id: p.id, name, description } });
                        setEditing("");
                        await refresh();
                        toast.success("Project updated");
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

        </section>
      ) : null}

      {/* Proposals */}
      {currentClient && currentProject ? (
        <section className="mt-12 mb-24">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
            Proposals · {currentProject.name}
          </h2>
          <UploadForm
            onUpload={async (title, file) => {
              const base64 = await fileToBase64(file);
              await upload({
                data: {
                  projectId: currentProject.id,
                  title,
                  slug: slugify(title),
                  fileName: file.name,
                  contentType: file.type || "application/octet-stream",
                  fileBase64: base64,
                },
              });
              await refresh();
              toast.success("Proposal uploaded");
            }}
          />
          {projectProposals.length > 1 ? (
            <p className="mt-4 text-xs text-muted-foreground">
              Drag the ⠿ handle to reorder how proposals appear to the client.
            </p>
          ) : null}
          <div className="mt-2 grid gap-2">
            {projectProposals.map((p, index) => (
              <div
                key={p.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = Number(e.dataTransfer.getData("text/plain"));
                  if (Number.isNaN(from) || from === index) return;
                  void moveProposal(from, index);
                }}
                className={`rounded-lg border border-border bg-card p-4 ${
                  dragIndex === index ? "opacity-50" : ""
                }`}
              >

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span
                      draggable
                      onDragStart={(e) => {
                        setDragIndex(index);
                        e.dataTransfer.setData("text/plain", String(index));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setDragIndex(null)}
                      title="Drag to reorder"
                      aria-label="Drag to reorder"
                      className="cursor-grab select-none px-1 text-lg leading-none text-muted-foreground active:cursor-grabbing"
                    >
                      ⠿
                    </span>
                    <div>
                      <span className="block font-display text-lg text-foreground">{p.title}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {p.kind === "html" ? "Web proposal" : "Document"} · /{p.slug}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 text-xs">
                    <a
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`/clients/${currentClient.slug}/projects/${currentProject.slug}/${p.slug}?t=${currentClient.access_token}`}
                    >
                      Open
                    </a>
                    <button
                      className="underline"
                      onClick={() => setEditing(editing === p.id ? "" : p.id)}
                    >
                      {editing === p.id ? "Cancel" : "Edit"}
                    </button>
                    <button
                      className="text-destructive underline"
                      onClick={async () => {
                        if (!confirm(`Delete ${p.title}?`)) return;
                        await rmProposal({ data: { id: p.id } });
                        await refresh();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {editing === p.id ? (
                  <EditForm
                    initial={[p.title]}
                    placeholders={["Proposal title"]}
                    onSave={async ([title]) => {
                      await editProposal({ data: { id: p.id, title } });
                      setEditing("");
                      await refresh();
                      toast.success("Proposal updated");
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-4xl px-4 py-16">{children}</div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const inputCls =
  "rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground";
const btnCls =
  "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60";

function NewClientForm({ onCreate }: { onCreate: (name: string, notes: string) => Promise<void> }) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-4 flex flex-wrap gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onCreate(name.trim(), notes.trim());
          setName("");
          setNotes("");
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Failed");
        } finally {
          setBusy(false);
        }
      }}
    >
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name" className={inputCls} />
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className={`${inputCls} flex-1`} />
      <button disabled={busy} className={btnCls}>Add client</button>
    </form>
  );
}

function NewProjectForm({
  onCreate,
}: {
  onCreate: (name: string, description: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-4 flex flex-wrap gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onCreate(name.trim(), description.trim());
          setName("");
          setDescription("");
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Failed");
        } finally {
          setBusy(false);
        }
      }}
    >
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" className={inputCls} />
      <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className={`${inputCls} flex-1`} />
      <button disabled={busy} className={btnCls}>Add project</button>
    </form>
  );
}

function UploadForm({ onUpload }: { onUpload: (title: string, file: File) => Promise<void> }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-4 flex flex-wrap items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
          toast.error("File is larger than 8 MB");
          return;
        }
        setBusy(true);
        try {
          await onUpload(title.trim() || file.name, file);
          setTitle("");
          setFile(null);
          (e.target as HTMLFormElement).reset();
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Upload failed");
        } finally {
          setBusy(false);
        }
      }}
    >
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Proposal title" className={inputCls} />
      <input
        type="file"
        required
        accept=".html,.htm,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="text-sm text-muted-foreground"
      />
      <button disabled={busy || !file} className={btnCls}>
        {busy ? "Uploading…" : "Upload proposal"}
      </button>
    </form>
  );
}

function EditForm({
  initial,
  placeholders,
  onSave,
}: {
  initial: string[];
  placeholders: string[];
  onSave: (values: string[]) => Promise<void>;
}) {
  const [values, setValues] = useState<string[]>(initial);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave(values.map((v) => v.trim()));
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Could not save");
        } finally {
          setBusy(false);
        }
      }}
    >
      {values.map((value, i) => (
        <input
          key={i}
          required={i === 0}
          value={value}
          placeholder={placeholders[i]}
          onChange={(e) =>
            setValues((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
          }
          className={`${inputCls} ${i === 0 ? "" : "flex-1"}`}
        />
      ))}
      <button disabled={busy} className={btnCls}>
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
}
